
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { clearAllCaches } from "./services/cacheService";
import { cancelAllRequests } from "./requestUtils";
import { logger } from "@/utils/logger";
import { keyStorage } from "@/utils/crypto/secureKeyStorage";

/**
 * Get the current user ID
 */
export async function getCurrentUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    logger.error("Error getting session:", error);
    throw error;
  }

  if (!data.session?.user?.id) {
    throw new Error("User not authenticated");
  }

  return data.session.user.id;
}

/**
 * Get the current user ID synchronously
 * Use this only when you know the user is authenticated
 */
export function getCurrentUserIdSync(): string | null {
  const localStorageSession = localStorage.getItem('supabase.auth.token');

  if (!localStorageSession) {
    return null;
  }

  try {
    const session = JSON.parse(localStorageSession);
    return session?.currentSession?.user?.id || null;
  } catch (error) {
    logger.error("Error parsing auth token:", error);
    return null;
  }
}

/**
 * Get the current user details
 */
export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    logger.error("Error getting session:", error);
    throw error;
  }

  if (!data.session?.user) {
    return null;
  }

  const userId = data.session.user.id;
  const email = data.session.user.email;

  return {
    id: userId,
    email,
    name: data.session.user.user_metadata?.name
  };
}

/**
 * Get the email of the currently authenticated user
 */
export async function getCurrentUserEmail(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.email || null;
  } catch (error) {
    logger.error("Error getting current user email:", error);
    return null;
  }
}

/**
 * Login user with email and password
 */
export async function login({ email, password, captchaToken }: { email: string; password: string; captchaToken?: string | null }) {

  // Use the Edge Function to enforce strict rate limiting (5 attempts/10 mins)
  // This uses the user's Upstash Redis configuration
  const { data, error } = await supabase.functions.invoke('sign-in-with-captcha', {
    body: {
      email,
      password,
      hCaptchaToken: captchaToken || undefined // Function expects hCaptchaToken
    }
  });

  if (error) {
    logger.error("Login Edge Function error:", error);

    let errorMessage = "Authentication failed";

    try {
      const errAny = error as any;

      // 1. Prioritize extracting JSON body from context (Supabase FunctionsHttpError)
      if (errAny.context && typeof errAny.context.json === 'function') {
        try {
          const body = await errAny.context.json();
          if (body && body.error) {
            errorMessage = body.error;
          } else if (body && body.message) {
            errorMessage = body.message;
          }
        } catch (jsonErr) {
          logger.error("Could not parse error context JSON:", jsonErr);
        }
      }
      // 2. Fallback to parsing message if it's JSON
      else if (errAny.message) {
        try {
          const parsed = JSON.parse(errAny.message);
          if (parsed.error) errorMessage = parsed.error;
          else if (parsed.message) errorMessage = parsed.message;
          else errorMessage = errAny.message;
        } catch {
          errorMessage = errAny.message;
        }
      }
      // 3. Last fallback to generic message if it's NOT the unhelpful SDK string
      if (errorMessage === "Edge Function returned a non-2xx status code" || errorMessage === "Authentication failed") {
        if (errAny.message && errAny.message !== "Edge Function returned a non-2xx status code") {
          errorMessage = errAny.message;
        }
      }
    } catch (e) {
      logger.error("Error parsing edge function error:", e);
    }

    throw new Error(errorMessage);
  }

  // Edge Function returns { user, session } on success
  if (data?.session) {
    // Manually set the session in the client so the app knows we are logged in
    const { error: sessionError } = await supabase.auth.setSession(data.session);
    if (sessionError) {
      logger.error("Failed to set session from Edge Function:", sessionError);
      throw sessionError;
    }
    return data;
  } else if (data?.error) {
    // Functional error returned in body (e.g. rate limit 429)
    throw new Error(data.error);
  }

  return data;
}

/**
 * Sign up a new user
 */
export async function signup({ email, password, name }: { email: string; password: string; name: string }) {
  // Create a new user
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        name,
      },
    }
  });

  if (error) {
    logger.error("Signup error:", error);
    throw error;
  }

  return data;
}

/**
 * Update user's display name
 */
export async function updateUserName(name: string) {
  try {
    // First update the user's metadata in auth system
    const { data: authData, error: authError } = await supabase.auth.updateUser({
      data: { name }
    });

    if (authError) {
      logger.error("Auth update error:", authError);
      throw authError;
    }

    // Then update the user's name in the profiles table
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ name })
      .eq('id', authData.user.id);

    if (profileError) {
      logger.error("Profile update error:", profileError);
      throw profileError;
    }

    return authData;
  } catch (error) {
    logger.error("Update user name error:", error);
    throw error;
  }
}

/**
 * Logout user and redirect to home page
 * Thoroughly clears all auth data and redirects to welcome page
 * Important: This function has been improved to prevent access control errors during logout
 * Now includes IP binding cleanup for PC sessions
 */
export async function logout() {
  logger.info("Starting improved logout process...");

  // STEP 1: Set a flag to prevent new requests during logout
  try {
    sessionStorage.setItem('logout_in_progress', 'true');
  } catch (e) {
    logger.error("Error setting logout flag:", e);
  }

  // STEP 1.5: Delete cookie consent from database
  try {
    // Dynamic import to avoid circular dependencies
    const { CookieConsentService } = await import("@/services/CookieConsentService");
    await CookieConsentService.deleteConsent();
    logger.info("Cookie consent deleted from database");
  } catch (consentError) {
    logger.error("Error deleting cookie consent:", consentError);
  }

  // STEP 2: Handle session-specific logout (PWA vs PC)
  try {
    const { isPwaMode } = await import("@/utils/pwaUtils");

    if (isPwaMode()) {
      // PWA logout: Delete token from database (CASCADE will handle IP binding)
      logger.info("PWA mode: Deleting token via /logout endpoint");
      const pwaToken = localStorage.getItem('pwa_auth_token');

      if (pwaToken) {
        const { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } = await import("@/integrations/supabase/client");

        const response = await fetch(`${SUPABASE_URL}/functions/v1/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ token: pwaToken }),
        });

        if (response.ok) {
          logger.info("PWA token deleted successfully (CASCADE cleaned IP binding)");
        } else {
          logger.error("Failed to delete PWA token:", await response.text());
        }
      }

      // Clear heartbeat key from IndexedDB for PWA
      await keyStorage.clearHeartbeatKey();
    } else {
      // PC logout: Use new logoutPcSession (handles IP binding + JWT invalidation)
      logger.info("PC mode: Using logoutPcSession for IP binding cleanup");
      try {
        const { logoutPcSession } = await import("@/utils/auth/sessionHeartbeat");
        await logoutPcSession();
        logger.info("PC session logged out successfully");
        // logoutPcSession already clears localStorage/sessionStorage/heartbeat
        // Skip to redirect
        window.location.href = "/";
        return;
      } catch (pcLogoutError) {
        logger.error("Error in logoutPcSession:", pcLogoutError);
        // Continue with fallback cleanup
      }
    }
  } catch (sessionLogoutError) {
    logger.error("Error during session-specific logout:", sessionLogoutError);
  }

  // STEP 3: Cancel all pending requests to prevent access control errors
  logger.info("Canceling all pending network requests...");
  cancelAllRequests();

  // STEP 4: Clear all caches to prevent quota issues and data leakage
  try {
    await clearAllCaches();
    logger.info("All caches cleared");
  } catch (cacheError) {
    logger.error("Error clearing caches:", cacheError);
  }

  // STEP 6: Clear all localStorage and sessionStorage
  try {
    localStorage.clear();
    sessionStorage.clear();
    logger.info("Browser storage cleared");
  } catch (storageError) {
    logger.error("Error clearing storage:", storageError);
  }

  // STEP 7: Clear all cookies
  try {
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    logger.info("Cookies cleared");
  } catch (cookieError) {
    logger.error("Error clearing cookies:", cookieError);
  }

  // STEP 8: Attempt to sign out from Supabase (if not already done)
  try {
    const { isPwaMode } = await import("@/utils/pwaUtils");

    if (isPwaMode()) {
      logger.info("PWA mode: Skipping supabase.auth.signOut() to preserve PC sessions");
    } else {
      logger.info("Signing out from Supabase...");
      const { error } = await supabase.auth.signOut({
        scope: 'global'
      });

      if (error) {
        logger.error("Supabase logout error:", error);
      } else {
        logger.info("Supabase signOut completed successfully");
      }
    }
  } catch (authError) {
    logger.error("Error during signout:", authError);
  }

  // STEP 9: Clear browser caches if available
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      logger.info("Browser caches cleared");
    } catch (cacheError) {
      logger.error("Error clearing browser caches:", cacheError);
    }
  }

  logger.info("Logout successful, all authentication data cleared");
  toast.success("You have been logged out successfully");

  // STEP 10: Force redirect to welcome page
  window.location.href = "/";
}

/**
 * Delete account data and logout (GDPR Right to Erasure)
 */
export async function deleteAccount() {
  logger.info("Starting account deletion process...");

  try {
    // 1. Call the database wipe RPC (no arguments needed)
    const { data, error: wipeError } = await supabase.rpc('wipe_user_account_data');

    if (wipeError) {
      logger.error("Error wiping account data:", wipeError);
      logger.error("Error details:", JSON.stringify(wipeError, null, 2));
      throw wipeError;
    }

    logger.info("Account data wiped successfully from database", data);

    // 2. Perform regular logout (clears local state, cookies, tokens)
    await logout();

    return true;
  } catch (error) {
    logger.error("Error deleting account:", error);
    // Log full error for debugging
    console.error("Full deletion error:", error);
    throw error;
  }
}

